import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '@/layout/AppShell';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { usePrefsEffects } from '@/lib/prefs';
import { useSession } from '@/store/session';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { InvitePage } from '@/pages/auth/InvitePage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { ComingSoonPage, NotFoundPage } from '@/pages/ComingSoon';
import { HomePage } from '@/pages/HomePage';
import { BillingPage } from '@/pages/BillingPage';
import { PayPage } from '@/pages/PayPage';
import { ReviewPage } from '@/pages/ReviewPage';
import { TasksPage } from '@/pages/TasksPage';
import { EpisodesPage } from '@/pages/episode/EpisodesPage';
import { EpisodePage } from '@/pages/episode/EpisodePage';
import { UploadPage } from '@/pages/episode/UploadPage';
import { TranscriptPage } from '@/pages/episode/TranscriptPage';
import { HostNotice } from '@/pages/auth/HostNotice';
import { isTenantBrand, useBranding } from '@/lib/branding';
import { Spinner } from '@/components/ui';

/** Publish & export and Integrations load on first visit, keeping the entry chunk inside its budget (ADR 0007). */
const PublishPage = lazy(() => import('@/pages/PublishPage').then((m) => ({ default: m.PublishPage })));
// Projects load on first visit too (amendment 25 part 2 added the YouTube audience setting and the entry hit its budget).
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
// Project settings (WS V): the sectioned edit page, with @castfeeds/multistep, outside the entry chunk.
const ProjectSettingsPage = lazy(() => import('@/pages/project/ProjectSettingsPage').then((m) => ({ default: m.ProjectSettingsPage })));
// Customers, Team and Approvals load on first visit (WS V follow-up): they are not on the first screen for most users,
// and the entry chunk keeps room under its 200 kB budget (ADR 0007).
const CustomersPage = lazy(() => import('@/pages/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const TeamPage = lazy(() => import('@/pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const ApprovalsPage = lazy(() => import('@/pages/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })));
// Settings (WS V follow-up): the section nav and the Branding save bar use @castfeeds/multistep, outside the entry chunk.
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const DevicesPage = lazy(() => import('@/pages/DevicesPage').then((m) => ({ default: m.DevicesPage })));
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })));
// Editor v2 (WS Z): the recent-edits list inside the shell, the full-screen editor outside it; both lazy with their strings.
const EditorListPage = lazy(() => import('@/pages/editor/EditorListPage').then((m) => ({ default: m.EditorListPage })));
const EditorPage = lazy(() => import('@/pages/editor/EditorPage').then((m) => ({ default: m.EditorPage })));
// Live Studio (WS W): the list, the operator screen and the public graphics output, each lazy with its strings.
const livePages = () => import('@/pages/live');
const LiveListPage = lazy(() => livePages().then((m) => ({ default: m.LiveListPage })));
const LiveStudioPage = lazy(() => livePages().then((m) => ({ default: m.LiveStudioPage })));
const LiveOutputPage = lazy(() => livePages().then((m) => ({ default: m.LiveOutputPage })));
// Calendar (WS X): its own chunk with its strings.
const CalendarPage = lazy(() => import('@/pages/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })));
/** Public, outside the shell; lazy with its strings (amendment 25 part 3). */
const DataDeletionPage = lazy(() => import('@/pages/DataDeletionPage').then((m) => ({ default: m.DataDeletionPage })));

// Brand kits (with camera rules, layouts and the music bed) load on demand to keep the entry chunk under its budget.
const BrandKitsPage = lazy(() => import('@/pages/BrandKitsPage').then((m) => ({ default: m.BrandKitsPage })));
const brandKits = (
  <Suspense fallback={<div style={{ padding: 18 }}><Spinner /></div>}>
    <BrandKitsPage />
  </Suspense>
);

/** Signed-in area: refresh the profile once (memberships may have changed) and require a token. */
function RequireAuth() {
  const token = useSession((s) => s.token);
  const setProfile = useSession((s) => s.setProfile);
  const location = useLocation();
  const me = useQuery({ queryKey: qk.me, queryFn: api.auth.me, enabled: !!token, staleTime: 60_000 });
  useEffect(() => {
    if (me.data) setProfile(me.data);
  }, [me.data, setProfile]);
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

function GuestOnly() {
  const token = useSession((s) => s.token);
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  return token ? <Navigate to={from} replace /> : <Outlet />;
}

export function App() {
  usePrefsEffects();
  const branding = useBranding((s) => s.branding);
  // White-label hosts (WS J): an inactive custom domain serves nothing; a review domain serves review links only.
  if (branding?.domain_status === 'inactive') return <HostNotice kind="inactive" />;
  if (isTenantBrand(branding) && branding.purpose === 'review') {
    return (
      <Routes>
        <Route path="/r/:token" element={<ReviewPage />} />
        <Route path="*" element={<HostNotice kind="review" />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route element={<GuestOnly />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/reset/:token" element={<ResetPasswordPage />} />
      <Route path="/r/:token" element={<ReviewPage />} />
      <Route path="/pay/:provider" element={<PayPage />} />
      <Route path="/data-deletion" element={<Suspense fallback={<Spinner />}><DataDeletionPage /></Suspense>} />
      <Route path="/live/:id/output" element={<Suspense fallback={null}><LiveOutputPage /></Suspense>} />
      <Route element={<RequireAuth />}>
        <Route path="editor/:editId" element={<Suspense fallback={<Spinner />}><EditorPage /></Suspense>} />
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          <Route path="projects" element={<Suspense fallback={<Spinner />}><ProjectsPage /></Suspense>} />
          <Route path="projects/:projectId" element={<Suspense fallback={<Spinner />}><ProjectsPage /></Suspense>} />
          <Route path="projects/:projectId/settings" element={<Suspense fallback={<Spinner />}><ProjectSettingsPage /></Suspense>} />
          <Route path="projects/:projectId/episodes/:episodeId" element={<EpisodePage />} />
          <Route path="projects/:projectId/episodes/:episodeId/upload" element={<UploadPage />} />
          <Route path="projects/:projectId/episodes/:episodeId/transcript" element={<TranscriptPage />} />
          <Route path="episodes" element={<EpisodesPage />} />
          <Route path="customers" element={<Suspense fallback={<Spinner />}><CustomersPage /></Suspense>} />
          <Route path="customers/:customerId" element={<Suspense fallback={<Spinner />}><CustomersPage /></Suspense>} />
          <Route path="brand-kits" element={brandKits} />
          <Route path="brand-kits/:kitId" element={brandKits} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="tasks/:taskId" element={<TasksPage />} />
          <Route path="editor" element={<Suspense fallback={<Spinner />}><EditorListPage /></Suspense>} />
          <Route path="approvals" element={<Suspense fallback={<Spinner />}><ApprovalsPage /></Suspense>} />
          <Route path="publish" element={<Suspense fallback={<Spinner />}><PublishPage /></Suspense>} />
          <Route path="calendar" element={<Suspense fallback={<Spinner />}><CalendarPage /></Suspense>} />
          <Route path="integrations" element={<Suspense fallback={<Spinner />}><IntegrationsPage /></Suspense>} />
          <Route path="approvals/:editId" element={<Suspense fallback={<Spinner />}><ApprovalsPage /></Suspense>} />
          <Route path="team" element={<Suspense fallback={<Spinner />}><TeamPage /></Suspense>} />
          <Route path="team/devices" element={<Suspense fallback={<Spinner />}><DevicesPage /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<Spinner />}><SettingsPage /></Suspense>} />
          <Route path="settings/:tab" element={<Suspense fallback={<Spinner />}><SettingsPage /></Suspense>} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="live" element={<Suspense fallback={<Spinner />}><LiveListPage /></Suspense>} />
          <Route path="live/:id" element={<Suspense fallback={<Spinner />}><LiveStudioPage /></Suspense>} />
          <Route path="soon/editor" element={<Navigate to="/editor" replace />} />
          <Route path="soon/calendar" element={<Navigate to="/calendar" replace />} />
          <Route path="soon/live" element={<Navigate to="/live" replace />} />
          <Route path="soon/:feature" element={<ComingSoonPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
